import { Component, ContentChild, HostBinding, inject, input, model, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { expandCollapseAnimation } from '../../../_animations/expand-collapse.animation';
import { SdBannerComponent } from '../../medias/banner/banner.component';
import { SdIconComponent } from '../../icons/icon/icon.component';
import { DeviceService } from '../../../_services/device.service';
import { ThemesTextEnum } from '../../../_enums/themes-text.enum';
import { SdTitleComponent } from '../../title/title.component';
import { PositionEnum } from '../../../_enums/position.enum';
import { SdHtmlComponent } from '../../html/html.component';
import { BrandsEnum } from '../../../_enums/brands.enum';
import { IconSizeEnum } from '../../../_enums/icon.enum';

@Component({
  selector: 'sd-accordion',
  templateUrl: './accordion.component.html',
  animations: [
    expandCollapseAnimation({
      anchor: 'expandCollapse',
      durationIn: 250,
      delayIn: 0,
      durationOut: 250,
      delayOut: 0
    })
  ],
  standalone: true,
  imports: [
    SdIconComponent,
    SdTitleComponent,
    SdHtmlComponent,
    SdBannerComponent
  ]
})
export class SdAccordionComponent {
  /**
   * If the panel is opened or closed
   */
  isOpened = model(false);
  /**
   * If the panel is opened or closed
   */
  isVisible = model(true);

  brand = input<BrandsEnum>();
  titleIcon = input<string>();
  titleInfo = input<string>();
  isDescription = input(false);
  /**
   * While opening an accordion, defined if the other accordion stay open (or not)
   */
  closeOthers = input<boolean>();
  /**
   * If the panel is opened or closed
   */
  isDrawer = input(false);
  /**
   * Detail Text to display in the group title bar
   */
  detail = input<string>();
  /**
   * Text to display in the group title bar
   */
  title = input<string>();
  /**
   * Custom class for title (brand--h3, brand--h4 ....)
   */
  titleCustomClass = input<string>();
  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  titleLevel = input(3);
  /**
   * Icon name of the icon for the opened state
   * * @param {string} icon name
   */
  iconNameOpened = input('icon-arrow-up');
  /**
   * Icon name of the icon for the closed state
   * * @param {string} icon name
   */
  iconNameClosed = input('icon-arrow-down');
  /**
   * Position of the icon who display the state opened or closed
   * * @param {PositionEnum} icon position left || right
   */
  iconPos = input<PositionEnum.left | PositionEnum.right>(PositionEnum.left);
  /**
   * Icon size
   * * @param {IconSizeEnum} icon size small || medium || large || logo;
   */
  iconSize = input<IconSizeEnum>(IconSizeEnum.small);
  /**
   * Defined if the accordion is nested or not, so the body dont have the default padding
   */
  isNested = input(false);
  /**
   * Defined the hidden title for the accordion (a11y)
   */
  buttonOpenHiddenTitle = input<string>();
  hasNoPadding = input(false);
  theme = input<ThemesTextEnum>(ThemesTextEnum.brand);
  isDisabled = input(false);
  class = input('');

  /**
   * Emitted when user clicks on group titlebar
   */
  toggled = output<boolean>();

  private readonly deviceService = inject(DeviceService);

  isMobile = toSignal(this.deviceService.isMobile);

  @ContentChild('accordionChild')
  accordionChild: any;

  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class(),
      this.isVisible() ? 'is-visible' : '',
      this.isOpened() ? 'is-opened' : '',
      this.isDrawer() ? 'is-drawer' : '',
      this.isDisabled() ? 'is-disabled' : '',
      this.iconPos() === PositionEnum.left ? 'is-icon-left' : 'is-icon-right',
      this.theme() ? 'is-theme-' + this.theme() : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  PositionEnum = PositionEnum;

  onClick() {
    if (!this.isDisabled()) {
      this.toggled.emit(this.isOpened());
    }
  }
}
